import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SpiderChart from "components/SpiderChart";
import MoralityStatusIndicator from "components/MoralityStatusIndicator";
import ValuesTestAccelerometer from "components/ValuesTestAccelerometer";
import logoQuadranteH from "assets/images/logo_koop/logo Quadrante H.png";
import fondoQuadranteH from "assets/images/Fondo_Quadrante-H.png";
import puzzleImage from "assets/images/Imagen_2.png";
import { REPORT_SECTIONS } from "../utils/constants";
import { PDF_STYLES } from "../utils/pdfConfig";

const ReportContent = ({ 
  candidatoData, 
  valuesTestData, 
  songTestData, 
  drawingTestData,
  psychIndicators,
  observacionesFinales,
  onObservacionesFinalesChange
}) => {
  const { nivelMoralidad, descripcionMoralidad } = psychIndicators;

  return (
    <MDBox sx={{ 
      ...PDF_STYLES.container, 
      position: 'relative',
      minHeight: '100%',
      width: '100%'
    }}>
      {/* Imagen de fondo - FIJA */}
      <MDBox
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${fondoQuadranteH})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.15,
          }
        }}
      />
      
      {/* Contenido principal */}
      <MDBox sx={{ 
        position: 'relative', 
        zIndex: 1,
        width: '100%',
        minHeight: '100%'
      }}>
        {/* Portada */}
        <MDBox 
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          textAlign="center"
          sx={{ 
            position: 'relative',
            pageBreakAfter: 'always',
            padding: '40px 20px'
          }}
        >
          <img 
            src={logoQuadranteH} 
            alt="Quadrante H Logo" 
            style={{
              maxWidth: "300px",
              width: "100%",
              height: "auto",
              marginBottom: "60px"
            }}
          />
        
          <MDTypography 
            variant="h4" 
            fontWeight="medium"
            color="dark"
            sx={{ fontSize: { xs: '20px', md: '28px' } }}
          >
            Perfilación Psicológica Estratégica
          </MDTypography>
          
          {/* Copyright en la portada */}
          <MDBox 
            position="absolute" 
            bottom="40px" 
            left="0"
            right="0"
            textAlign="center"
            px={2}
          >
            <MDTypography variant="caption" color="text" sx={{ fontSize: '12px' }}>
              © Copyright 2023-2026 Koop Tecnológica SAS BIC<br/>
              Quadrante H es una marca de Koop Tecnológica SAS BIC
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Página de índice y detalles del candidato */}
        <MDBox 
          minHeight="100vh"
          sx={{ 
            pageBreakAfter: 'always',
            position: 'relative',
            paddingBottom: '60px'
          }}
        >
          {/* Contenido principal en dos columnas */}
          <MDBox 
            display="flex" 
            flexDirection={{ xs: 'column', md: 'row' }}
            minHeight="calc(100vh - 250px)"
            px={{ xs: 2, md: 0 }}
          >
            {/* Columna izquierda - Detalles del Candidato */}
            <MDBox 
              width={{ xs: '100%', md: '50%' }}
              p={{ xs: 2, md: 4 }}
              display="flex"
              flexDirection="column"
            >
              <MDBox 
                border="2px solid #333"
                p={3}
                backgroundColor="#f3f0ff"
                borderRadius="8px"
                height="fit-content"
                mb={{ xs: 3, md: 0 }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="dark">
                  Detalles del Candidato:
                </MDTypography>
                
                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Nombre: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.nombreCompleto}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Género: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.genero || "No especificado"}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Identificación: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.identificacion}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Edad: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.edad}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Profesión: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.profesion}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Cargo al que aplica: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.cargoAplicado}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    Fecha de Evaluación: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.fechaEvaluacion}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography variant="body2" fontWeight="bold" display="inline">
                    E-mail: 
                  </MDTypography>
                  <MDTypography variant="body2" display="inline" ml={1}>
                    {candidatoData.email || "contacto@ejemplo.com"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* Columna derecha - Índice */}
            <MDBox 
              width={{ xs: '100%', md: '50%' }}
              p={{ xs: 2, md: 4 }}
              display="flex"
              flexDirection="column"
              position="relative"
            >
              {/* Índice */}
              <MDBox width="100%" textAlign="left">
                <MDTypography variant="h4" fontWeight="bold" mb={4} color="dark">
                  Dentro de este informe
                </MDTypography>

                <MDBox component="ol" sx={{ pl: 2 }}>
                  <MDBox component="li" mb={1}>
                    <MDTypography variant="body1" fontWeight="medium">
                      Datos del Candidato
                    </MDTypography>
                  </MDBox>

                  <MDBox component="li" mb={1}>
                    <MDTypography variant="body1" fontWeight="medium">
                      Propósito del Informe
                    </MDTypography>
                  </MDBox>

                  <MDBox component="li" mb={2}>
                    <MDTypography variant="body1" fontWeight="medium">
                      Resultados del Test Quadrante H
                    </MDTypography>
                    <MDBox component="ul" sx={{ pl: 3, mt: 1 }}>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Perfil de Personalidad
                        </MDTypography>
                      </MDBox>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Motivaciones y reacciones bajo Estrés
                        </MDTypography>
                      </MDBox>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Imagen Proyectada
                        </MDTypography>
                      </MDBox>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Dimensiones del Perfil Personal
                        </MDTypography>
                      </MDBox>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Índice de Integridad Laboral
                        </MDTypography>
                      </MDBox>
                      <MDBox component="li" mb={0.5}>
                        <MDTypography variant="body2">
                          Áreas de Desarrollo y Recomendaciones
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <MDBox component="li" mb={1}>
                    <MDTypography variant="body1" fontWeight="medium">
                      Resumen Ejecutivo
                    </MDTypography>
                  </MDBox>

                  <MDBox component="li" mb={1}>
                    <MDTypography variant="body1" fontWeight="medium">
                      Observaciones Finales
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              {/* Imagen de puzzle - Solo visible en desktop y no superpuesta */}
              <MDBox 
                sx={{
                  display: { xs: 'none', md: 'block' },
                  mt: 'auto',
                  textAlign: 'right',
                  pt: 2
                }}
              >
                <img 
                  src={puzzleImage} 
                  alt="Team Puzzle" 
                  style={{
                    width: '180px',
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>

      {/* NUEVA PÁGINA - Propósito del Informe */}
      <MDBox 
        sx={{ 
          pageBreakBefore: 'always',
          minHeight: '50vh',
          pt: 4
        }}
      >
        <Divider sx={{ my: 3 }} />

        {/* Propósito del Informe */}
        <MDBox mb={4} position="relative" px={{ xs: 2, md: 4 }}>
          <MDTypography variant="h5" fontWeight="bold" color="info" mb={2}>
            {REPORT_SECTIONS.PURPOSE.title}
          </MDTypography>
          <MDTypography variant="body2" textAlign="justify" mb={3}>
            {REPORT_SECTIONS.PURPOSE.content}
          </MDTypography>
          
          {/* Imagen de puzzle decorativa */}
          <MDBox 
            sx={{ 
              display: { xs: 'none', md: 'block' },
              float: 'right',
              ml: 2,
              mb: 2
            }}
          >
            <img 
              src={puzzleImage} 
              alt="Team Puzzle" 
              style={{
                width: '120px',
                height: 'auto',
                opacity: 0.8
              }}
            />
          </MDBox>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        {/* ¿Qué Hace el Test? */}
        <MDBox mb={4} px={{ xs: 2, md: 4 }}>
          <MDTypography variant="h5" fontWeight="bold" color="info" mb={2}>
            {REPORT_SECTIONS.TEST_DESCRIPTION.title}
          </MDTypography>
          <MDTypography variant="body2" textAlign="justify" mb={2}>
            {REPORT_SECTIONS.TEST_DESCRIPTION.content}
          </MDTypography>

          {/* Aspectos evaluados */}
          <MDBox ml={2}>
            {REPORT_SECTIONS.TEST_DESCRIPTION.aspects.map((aspect, index) => (
              <MDBox key={index} mb={3}>
                <MDTypography variant="body2" fontWeight="bold" mb={1}>
                  {aspect.title}
                </MDTypography>
                <MDBox ml={2}>
                  {aspect.points.map((point, pointIndex) => (
                    <MDTypography key={pointIndex} variant="body2" mb={1}>
                      • {point}
                    </MDTypography>
                  ))}
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        {/* Beneficios del Informe */}
        <MDBox mb={4} px={{ xs: 2, md: 4 }}>
          <MDTypography variant="h5" fontWeight="bold" color="info" mb={2}>
            {REPORT_SECTIONS.BENEFITS.title}
          </MDTypography>
          
          <MDBox ml={2}>
            {REPORT_SECTIONS.BENEFITS.items.map((benefit, index) => (
              <MDTypography key={index} variant="body2" mb={1}>
                <strong>• {benefit.title}</strong> {benefit.description}
              </MDTypography>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>

      {/* NUEVA PÁGINA - Cargo y Resultados */}
      <MDBox sx={{ pageBreakBefore: 'always', pt: 4 }}>
        <Divider sx={{ my: 3 }} />

        <MDBox mb={1} px={{ xs: 2, md: 4 }}>
          <MDTypography variant="h5" textAlign="center" fontWeight="bold" color="info" mb={1}>
            Cargo Solicitado 
          </MDTypography>
          <MDTypography variant="body2" textAlign="center">
            <strong>{candidatoData.cargoAplicado.charAt(0).toUpperCase() + candidatoData.cargoAplicado.slice(1)}</strong>
          </MDTypography>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        {/* Resultados del Test A - Diagrama de Araña */}
        <MDBox mb={4} px={{ xs: 2, md: 4 }}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Resultados
          </MDTypography>
          <MDTypography variant="h5" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Mapas de Comportamiento y Potencial
          </MDTypography>
          <Grid container spacing={3} justifyContent="center">
            {/* Primera fila - 2 gráficos arriba */}
            <Grid item xs={12} md={6}>
              <SpiderChart title="Yo Real (T)" size="medium" />
            </Grid>
            <Grid item xs={12} md={6}>
              <SpiderChart title="Motivación (M)" size="medium" />
            </Grid>
            {/* Segunda fila - 1 gráfico abajo centrado */}
            <Grid item xs={12} md={6} sx={{ mx: 'auto' }}>
              <SpiderChart title="Respuesta en Desafío (L)" size="medium" />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      {/* NUEVA PÁGINA - Dimensiones del Perfil */}
      <MDBox sx={{ pageBreakBefore: 'always', pt: 4, px: { xs: 2, md: 4 } }}>
        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Dimensiones del Perfil Personal
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" textAlign="center" mb={3}>
          Tiende a ser una persona muy atenta a los riesgos y a las posibles consecuencias de sus decisiones. Busca confirmación antes de actuar y puede experimentar tensión bajo presión, especialmente si percibe falta de certeza en los resultados.
        </MDTypography>
          
        <MDTypography variant="body2" textAlign="center" mb={4}>
          <strong>En el contexto laboral:</strong> aporta cautela y profundidad en la evaluación de alternativas, reduciendo errores en tareas críticas. No obstante, podría requerir apoyo en situaciones que demanden respuestas rápidas sin toda la información disponible.
        </MDTypography>

        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Motivación esencial
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" textAlign="center" mb={3}>
          Los resultados señalan aquello que mueve a la persona en su día a día: las razones profundas que sostienen su energía, lo que le da sentido al esfuerzo y lo mantiene constante incluso frente a los retos. Aquí se reflejan sus principales necesidades, sus anhelos y las condiciones que lo ayudan a comprometerse de verdad con su trabajo.
        </MDTypography>

        <MDTypography variant="body2" textAlign="center" mb={4}>
          En este caso, las personas con: ### se observa que lo que lo impulsa internamente está relacionado con ###
        </MDTypography>

        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Reacciones bajo Presión
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" textAlign="center" mb={3}>
          Los resultados muestran cómo reacciona esta persona en momentos de presión o tensión. Aquí se reflejan los cambios en su forma de actuar cuando las demandas aumentan, el tiempo se acorta o las situaciones se vuelven más complejas de lo habitual. Comprender estas reacciones es clave porque permiten anticipar qué puede ocurrir bajo estrés y qué tipo de apoyo o condiciones ayudan a que mantenga el equilibrio.
        </MDTypography>

        <MDTypography variant="body2" textAlign="center" mb={4}>
          En este caso, se observa que tiende a volverse impaciente, autoritario o demasiado orientado a ganar. Puede imponer decisiones rápidas sin considerar a otros. 
        </MDTypography>
      </MDBox>

      {/* NUEVA PÁGINA - Índice de Integridad */}
      <MDBox sx={{ pageBreakBefore: 'always', pt: 4, px: { xs: 2, md: 4 } }}>
        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Índice de Integridad Laboral (IIL)
          </MDTypography>
        </MDBox>
        
        {/* Estado general de la moralidad del candidato */}
        <MDBox mb={4}>
          <MoralityStatusIndicator 
            level={nivelMoralidad}
            description={descripcionMoralidad}
          />
        </MDBox>

        {/* Resultados del Test de Valores - Acelerómetros */}
        {valuesTestData && (
          <MDBox mb={4}>
            <ValuesTestAccelerometer testData={valuesTestData} testType="values" />
          </MDBox>
        )}

        {/* Resultados del Test de Canciones - Resonancia Emocional */}
        {songTestData && (
          <MDBox mb={4}>
            <ValuesTestAccelerometer testData={songTestData} testType="songs" />
          </MDBox>
        )}

        {/* Resultados del Test de Dibujos */}
        {drawingTestData && (
          <MDBox mb={4}>
            <ValuesTestAccelerometer testData={drawingTestData} testType="drawing" />
          </MDBox>
        )}

        <MDBox mb={4}>
          <MDTypography variant="body2" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Este instrumento no diagnostica ningún trastorno ni reemplaza una evaluación clínica completa. Es una herramienta orientativa para uso en selección de personal.
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* NUEVA PÁGINA - Recomendaciones */}
      <MDBox sx={{ pageBreakBefore: 'always', pt: 4, px: { xs: 2, md: 4 } }}>
        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Recomendaciones: Plan de Desarrollo y Estrategias de Entrenamiento
          </MDTypography>
        </MDBox>

        <MDTypography variant="body2" textAlign="left" mb={2}>
          <strong>Objetivo:</strong>     
        </MDTypography>
          
        <MDTypography variant="body2" textAlign="left" mb={4}>
          <strong>Estrategias recomendadas:</strong> 
        </MDTypography>

        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Resumen Ejecutivo
          </MDTypography>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        <MDBox mb={4}>
          <MDTypography variant="h3" fontWeight="bold" textAlign="center" color="info" mb={3}>
            Observaciones Finales
          </MDTypography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Ingrese las observaciones finales de la empresa..."
            value={observacionesFinales || ''}
            onChange={(e) => onObservacionesFinalesChange && onObservacionesFinalesChange(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8f9fa',
                '& fieldset': {
                  borderColor: '#e0e6ed',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </MDBox>

        {/* Footer */}
        <MDBox textAlign="center" mt={6} pt={3} borderTop="1px solid #eee">
          <MDTypography variant="caption" color="text">
            ©️ {new Date().getFullYear()} Quadrante H - Redefiniendo como las empresas eligen el talento
          </MDTypography>
        </MDBox>
      </MDBox>
      </MDBox>
    </MDBox>
  );
};

ReportContent.propTypes = {
  candidatoData: PropTypes.object.isRequired,
  valuesTestData: PropTypes.object,
  songTestData: PropTypes.object,
  drawingTestData: PropTypes.object,
  psychIndicators: PropTypes.object.isRequired,
  observacionesFinales: PropTypes.string,
  onObservacionesFinalesChange: PropTypes.func
};

export default ReportContent;
